import { colorForBackgroundColor } from './RGBColorToHex';

/**
 * Transforms a raw site/teamSite object into a standardized SiteDisplay object.
 * Replicates the logic from strike-payer for consistency.
 */
export const toSiteDisplay = (site) => {
  if (!site) return null;

  const {
    id,
    title,
    description,
    button,
    imageUrl,
    color,
    textColor,
    displayName,
    type,
    isApp,
    redirectLinks,
    templateTitle,
    templateDescription,
    templateButton,
  } = site;

  // 1. Resolve URL (priority: success redirect -> team url -> template link only when no team override)
  // When team has overridden (site.id = team site doc id), do NOT fall back to template link if url is empty
  const hasTeamOverride = !!(site.id || site.teamId);
  const url = redirectLinks?.success || site.url || (!hasTeamOverride ? site.link : null);

  // 2. Validate URL (deactivated sites have no valid url)
  if (!url || !url.startsWith('http')) {
    return null;
  }

  if (site.status === 'deactivated') {
    return null;
  }

  // 3. Resolve Button Text
  // Mimic i18next translations used in strike-payer
  const defaults = {
    review: 'Review',
    booking: 'Book now',
    book: 'Book now',
    affiliate: 'Shop now',
    shop: 'Shop now',
    loyalty: 'Join us',
    social: 'Follow',
    app: 'Download'
  };
  const buttonText = isApp
    ? (templateButton || defaults.app)
    : (button || templateButton || defaults[type] || 'Visit');

  // 4. Resolve Image (Social sites always show a big icon, Apps always show a big icon)
  const isShowImage = isApp || type === 'social';
  const appPlaceholderIcon = 'https://pay.strikepay.co/images/strikepay_qr_icon.png';
  const resolvedImageUrl = imageUrl || (isApp ? appPlaceholderIcon : undefined);

  // 5. Resolve Colors (using exact field names from strike-payer)
  const finalColor = color || site.templateColor || site.rgbColor || '#1A1A1A';
  const finalTextColor = textColor || colorForBackgroundColor(finalColor);

  const defaultHeadlineByType = {
    review: 'Leave us a review',
    booking: 'Book your next appointment',
    book: 'Book your next appointment',
    affiliate: 'Shop our recommendations',
    shop: 'Shop our recommendations',
    loyalty: 'Join our loyalty program',
    social: 'Follow us',
  };
  const headline = isApp
    ? (templateTitle || templateDescription || title || null)
    : (title || defaultHeadlineByType[type] || null);

  return {
    id,
    headline,
    displayName,
    imageUrl: resolvedImageUrl,
    url,
    buttonText,
    color: finalColor,
    textColor: finalTextColor,
    isApp: !!isApp,
    isShowImage,
    type,
  };
};

/**
 * Sorts an array of SiteDisplay objects according to the success page priority.
 */
export const sortSitesForPreview = (sites) => {
  const priority = {
    review: 1,
    book: 2,
    booking: 2,
    affiliate: 3,
    shop: 3,
    loyalty: 4,
    social: 5,
  };

  return [...sites]
    .map(toSiteDisplay)
    .filter(Boolean)
    .sort((a, b) => {
      const pA = priority[a.type] || 99;
      const pB = priority[b.type] || 99;
      return pA - pB;
    });
};
